import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, View } from 'react-native';
import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/hooks/useResponsiveDimensions';
import { StackScreenProps } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { SquareGrid } from '@/components/SquareGrid';
import { useStoreState } from '@/stores/store';
import { Text } from '@/components/Text';
import { newDownloadResults } from '@/api/services/results';
import OfflineImage from '@/components/SharedComponents/OfflineImage/OfflineImage';
import { useDialog } from '@/hooks/useDialog';
import type { DialogId } from '@/components/ui/shared/Dialog/types';
import { useCommonStyles } from '@/hooks/useCommonStyles';
import encryptedStorage from '@/stores/encryptedStorage';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { TextLink } from '@/components/TextLink';
import { ToggleTherapistCanSeeResults } from './ToggleTherapistCanSeeResults';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { Icon } from '@/components/Icon';
import { isSetStorageKey } from '@/utils/setStorageKey';
import store from '@/stores/store';
import colors from '@/config/colors';
import theme from '@/config/theme';

import { styles } from './styles';

export const Results = (
  props: StackScreenProps<
    TherapistStackParamList | PatientStackParamList,
    'ResultsScreen'
  >,
) => {
  const [sets, setSets] = useState<any[]>([]);
  const { showDialog, removeDialog } = useDialog();
  // Track dialogs we push so they can't be orphaned on the app-root stack if
  // the screen unmounts before they're dismissed.
  const dialogIdsRef = useRef<DialogId[]>([]);

  const { navigation } = props;
  const { accessToken } = useStoreState(
    (state) => state.signedInUser,
    () => true,
  );

  const commonStyles = useCommonStyles();
  const responsiveFontSize = useResponsiveFontSize(1);
  let isLandscape = useResponsiveHeight(100) < useResponsiveWidth(100);
  const getResponsiveFontSize = (factor: number) => {
    return responsiveFontSize * factor;
  };

  function useResponsiveSize(number: number) {
    if (isLandscape) {
      return useResponsiveWidth(number);
    } else {
      return useResponsiveHeight(number);
    }
  }

  const dialogPadding = useResponsiveWidth(2.8);

  const showErrorMessage = useCallback(() => {
    const id = showDialog({
      title: 'Fehler beim Speichern.',
      content:
        'Zum Speichern der Einstellung ist Internet erforderlich. Bitte überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
      confirmAction: {
        label: 'Schließen',
        onPress: (_e, onClose) => onClose?.(),
      },
    });
    dialogIdsRef.current.push(id);
  }, [showDialog]);

  let responsiveStyles = StyleSheet.create({
    container: {
      paddingVertical: useResponsiveFontSize(1),
      // height: Dimensions.get('window').height,r
      flexDirection: 'column',
    },

    exerciseSetImageCardBorder: {
      borderWidth: useResponsiveSize(0.3),
      // padding: useResponsiveSize(0.2),
      borderRadius: useResponsiveSize(0.2),
    },

    exercisesetTitleText: {
      textAlign: 'center',
      fontSize: useResponsiveSize(1.5),
    },
    setBoxesWrapper: {
      width: useResponsiveWidth(50),
      height: getResponsiveFontSize(28),
    },
    switchLabelStyle: {
      fontWeight: '700',
      fontSize: getResponsiveFontSize(1.5),
      marginVertical: getResponsiveFontSize(0.6),
    },
    footerTextStyle: {
      textAlign: 'center',
      fontSize: getResponsiveFontSize(1.3),
    },
    responsiveSwitcherWrapperStyle: {
      marginVertical: useResponsiveFontSize(1),
    },
    footerContainer: {
      paddingBottom: getResponsiveFontSize(2),
      alignItems: 'center',
      justifyContent: 'center',
    },
    wrapperPadding: {
      padding: useResponsiveWidth(1),
    },
  });

  const determineSetType = (setType: string) => {
    if (setType === 'textset') {
      return 'Texte';
    } else if (setType === 'wordset') {
      return 'Wörter';
    } else if (setType === 'sentenceset') {
      return 'Sätze';
    }
  };

  const navigateToResult = useCallback(
    (options: {
      patientId: any;
      exerciseClass: string;
      setId: any;
      patientName: string;
      exerciseSet: string | undefined;
      exerciseSetName: any;
    }) => {
      navigation.navigate('ResultDetailScreen', options);
    },
    [],
  );

  // add exercise sets to be rendered as SquareGrid
  const exerciseSets = useMemo(() => {
    const user = store.getState().signedInUser?.user;
    return (sets ?? [])
      .map((set, key) => {
        if (!set?.exerciseClass || !set?.activeWordset) {
          return null;
        }
        const type = `${set.exerciseClass}set`;
        const activeWordset = set.activeWordset?.[type];
        const items = activeWordset?.[type + 'Items'] ?? [];
        const appDisplayName = activeWordset?.appDisplayName;

        const onPress = () => {
          const fullName = user
            ? `${user.firstName ?? ''} ${user.lastName ?? ''}`
            : 'Gast';
          const options = {
            patientId: set.patientId,
            exerciseClass: type,
            setId: activeWordset._id,
            patientName: fullName,
            exerciseSet: determineSetType(type),
            exerciseSetName: appDisplayName,
          };
          navigateToResult(options);
        };

        if (!items.length) {
          return null;
        }
        const first = items[0] ?? {};
        const imageUri =
          first.word?.pictures?.[0] ?? first.sentence?.pictures?.[0] ?? null;

        if (type !== 'textset') {
          return (
            <PressableNeolexon
              testID={determineSetType(type)?.toLowerCase()}
              key={key}
              style={[
                styles.width90Height90,
                styles.exercisesetCard,
                Platform.OS === 'web'
                  ? styles.centerCardContainer
                  : styles.paddingForMobileCardContainer,
              ]}
              onPress={onPress}
            >
              <View>
                <Text
                  style={[
                    responsiveStyles.exercisesetTitleText,
                    { color: colors.darkGrey },
                  ]}
                  numberOfLines={1}
                >
                  {appDisplayName ? appDisplayName : determineSetType(type)}
                </Text>
              </View>
              <View style={[styles.setImageWrapper]}>
                <View style={[styles.setImageWrapper]}>
                  <View
                    style={[
                      styles.imageStackBackground,
                      responsiveStyles.exerciseSetImageCardBorder,
                    ]}
                  />
                </View>
                <View
                  style={[
                    styles.exerciseSetImageCard,
                    responsiveStyles.exerciseSetImageCardBorder,
                  ]}
                >
                  {type === 'textset' ? (
                    <Image
                      source={require('@/assets/text_icon.png')}
                      style={[
                        styles.exerciseSetImageStyle,
                        styles.imageBorderRadius,
                      ]}
                    />
                  ) : (
                    <OfflineImage
                      source={{ uri: imageUri }}
                      style={[
                        styles.exerciseSetImageStyle,
                        styles.imageBorderRadius,
                      ]}
                    />
                  )}
                </View>
              </View>
            </PressableNeolexon>
          );
        }
      })
      .filter(Boolean);
  }, [sets]);

  const showMobileRedirectDialog = useCallback(() => {
    const id = showDialog({
      title: 'Nur in der Browser-App möglich',
      confirmAction: {
        label: 'Schließen',
        onPress: (_e, onClose) => onClose?.(),
      },
      contentComponent: () => (
        <View
          style={[styles.mobileRedirectWrapper, { padding: dialogPadding }]}
        >
          <Text style={{ fontSize: theme.fontSizes.xl, textAlign: 'center' }}>
            Das Herunterladen der App-Daten ist in der Browser-App am Computer
            auf{' '}
          </Text>
          <TextLink
            href="https://aphasie.neolexon.de"
            style={[styles.linkStyle, { fontSize: theme.fontSizes.xl }]}
          >
            https://aphasie.neolexon.de
          </TextLink>
          <Text style={{ fontSize: theme.fontSizes.xl, textAlign: 'center' }}>
            {' '}
            unter Ergebnisse ansehen möglich.
          </Text>
        </View>
      ),
    });
    dialogIdsRef.current.push(id);
  }, [showDialog, dialogPadding]);

  const onDownloadModalShow = useCallback(() => {
    if (Platform.OS === 'web') {
      const id = showDialog({
        title: 'App-Daten herunterladen',
        content:
          'Wollen Sie Ihre App-Daten wirklich herunterladen? Dies sollte nur auf eigenen Geräten durchgeführt werden, um zu verhindern, dass Unbefugte Zugriff auf die heruntergeladenen Daten erhalten.',
        showCancelButton: true,
        cancelAction: {
          label: 'Abbrechen',
          onPress: (_e, onClose) => onClose?.(),
        },
        confirmAction: {
          label: 'Herunterladen',
          onPress: (_e, onClose) => {
            accessToken && newDownloadResults(accessToken);
            onClose?.();
          },
        },
      });
      dialogIdsRef.current.push(id);
    } else {
      showMobileRedirectDialog();
    }
  }, [showDialog, accessToken, showMobileRedirectDialog]);

  // Remove any dialogs we opened when leaving the screen, so they don't linger
  // on the app-root stack after navigation.
  useEffect(() => {
    const ids = dialogIdsRef.current;
    return () => {
      ids.forEach((id) => removeDialog(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const generateSets = async () => {
      const storageKeys = await encryptedStorage().listItems();
      const setsPromises = [];
      for (let key of storageKeys) {
        if (!isSetStorageKey(key)) {
          continue;
        }
        setsPromises.push(encryptedStorage().getItem(key));
      }
      const sets = await Promise.all(setsPromises);
      setSets(sets);
    };
    generateSets();
  }, []);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <AppHeaderAction
          primary
          onPress={() => navigation.goBack()}
          icon={<Icon name="chevronLeft" scale={1.2} />}
          text="Zurück"
        />
      ),
    });
  }, []);

  return (
    <ScrollView style={styles.full1}>
      <View style={responsiveStyles.container}>
        <View style={styles.header}>
          <View style={[styles.full1]} />
          <Text style={[commonStyles.titles, styles.textHeader, styles.full2]}>
            Statistiken der aktiven Übungssets anzeigen
          </Text>
          {/* Empty View (not to position the arrow absolute) */}
          <View style={styles.full1} />
        </View>
        <View
          style={[
            styles.allowTherapistSwitchContainer,
            responsiveStyles.responsiveSwitcherWrapperStyle,
          ]}
        >
          <View
            style={[
              styles.allowTherapistSwitchWrapper,
              responsiveStyles.wrapperPadding,
            ]}
          >
            <Text style={responsiveStyles.switchLabelStyle}>
              Ergebnisse mit dem verbundenen Therapeuten-Account teilen
            </Text>
            <ToggleTherapistCanSeeResults showErrorMessage={showErrorMessage} />
          </View>
        </View>
        <View style={styles.centerPosition}>
          <View style={responsiveStyles.setBoxesWrapper}>
            <SquareGrid items={exerciseSets} rows={2} columns={2} />
          </View>
        </View>
      </View>
      <View style={responsiveStyles.footerContainer}>
        <View style={responsiveStyles.footerContainer}>
          <Text
            style={[responsiveStyles.footerTextStyle, { textAlign: 'center' }]}
          >
            Download der{' '}
            <Text
              style={[styles.linkStyle, responsiveStyles.footerTextStyle]}
              onPress={onDownloadModalShow}
            >
              Übungsergebnisse und Übungsdauer
            </Text>{' '}
            als interoperables Bundle des MIO DiGA Toolkit
          </Text>
        </View>
      </View>
    </ScrollView>
  );
};
